import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbLabel } from '@store/breadcrumbLabels';
import {
  contextService,
  Context,
  ImportScanInput,
  ImportScanHistoryResult,
} from '@services/contextService';
import { organizationService } from '@services/organizationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

// A single scan from an exported history file. Mirrors what the CLI/standalone
// tool emits and what the import-scan-history endpoint accepts (REV-411).
const scanSchema = z.object({
  url: z.string().url(),
  source: z.string().optional(),
  scannedAt: z.string().optional(),
  commitSha: z.string().optional(),
  branch: z.string().optional(),
  prNumber: z.number().optional(),
  externalId: z.string().optional(),
  findings: z.array(z.unknown()).optional(),
});

// Accept either a bare array of scans or a wrapped `{ scans: [...] }` document.
const batchSchema = z.union([z.array(scanSchema), z.object({ scans: z.array(scanSchema) })]);

const MAX_SCANS = 1000;

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file);
  });
}

function ImportScanHistoryPage() {
  const { t } = useTranslation();
  const { slug, contextId } = useParams<{ slug: string; contextId: string }>();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [context, setContext] = useState<Context | null>(null);
  // REV-939: the tracked-place crumb above this page names the place — but
  // only the place loaded for the URL now on screen (`crumbLoadedFor`); the
  // page stays mounted when only :contextId changes.
  const [crumbLoadedFor, setCrumbLoadedFor] = useState<string | null>(null);
  useBreadcrumbLabel(
    slug && contextId ? `/orgs/${slug}/contexts/${contextId}` : undefined,
    context && crumbLoadedFor ? { path: crumbLoadedFor, label: context.name } : null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ImportScanInput[] | null>(null);
  const [findingsPreview, setFindingsPreview] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportScanHistoryResult | null>(null);

  const fetchContext = useCallback(async () => {
    if (!slug || !contextId) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      const ctx = await contextService.getById(org.id, contextId);
      setContext(ctx);
      setCrumbLoadedFor(`/orgs/${slug}/contexts/${contextId}`);
    } catch {
      setError(t('importHistory.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [slug, contextId, t]);

  useEffect(() => {
    void fetchContext();
  }, [fetchContext]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file later by clearing the input value.
    event.target.value = '';
    if (!file) return;

    setParseError(null);
    setResult(null);
    setSubmitError(null);
    setParsed(null);
    setFileName(file.name);

    let raw: string;
    try {
      raw = await readFileText(file);
    } catch {
      setParseError(t('importHistory.errors.readFailed'));
      return;
    }

    let json: unknown;
    try {
      json = JSON.parse(raw);
    } catch {
      setParseError(t('importHistory.errors.invalidJson'));
      return;
    }

    const validation = batchSchema.safeParse(json);
    if (!validation.success) {
      setParseError(t('importHistory.errors.invalidFormat'));
      return;
    }

    const scans = Array.isArray(validation.data) ? validation.data : validation.data.scans;
    if (scans.length === 0) {
      setParseError(t('importHistory.errors.empty'));
      return;
    }
    if (scans.length > MAX_SCANS) {
      setParseError(t('importHistory.errors.tooMany', { max: MAX_SCANS }));
      return;
    }

    const totalFindings = scans.reduce((sum, s) => sum + (s.findings?.length ?? 0), 0);
    setParsed(scans);
    setFindingsPreview(totalFindings);
  };

  const handleImport = async () => {
    if (!orgId || !contextId || !parsed) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    try {
      const summary = await contextService.importScanHistory(orgId, contextId, parsed);
      setResult(summary);
    } catch {
      setSubmitError(t('importHistory.errors.importFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={280} height={48} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        {/* REV-732: goes to a URL, so it is a link. */}
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} component={RouterLink} to="/orgs">
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  const contextHref = `/orgs/${slug}/contexts/${contextId}`;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('importHistory.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        {t('importHistory.description', { context: context?.name ?? '' })}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('importHistory.uploadHeading')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('importHistory.uploadHelp')}
          </Typography>

          {/* REV-496: document what the export file must contain, where it
              comes from, and its format so the picker isn't context-free. */}
          <Box component="details" sx={{ mb: 2 }}>
            <Box
              component="summary"
              sx={{ cursor: 'pointer', color: 'primary.main', mb: 1, width: 'fit-content' }}
            >
              {t('importHistory.format.toggle')}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('importHistory.format.intro')}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldUrl')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldSource')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldScannedAt')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldProvenance')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldExternalId')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('importHistory.format.fieldFindings')}
              </Typography>
            </Box>
            <Typography variant="subtitle2" component="p" gutterBottom>
              {t('importHistory.format.exampleHeading')}
            </Typography>
            <Box
              component="pre"
              // REV-704: named so a test can assert this element's actual
              // computed contrast, not just the palette tokens behind it.
              data-testid="import-example-block"
              sx={{
                m: 0,
                p: 2,
                // REV-704: bgcolor was set and color was NOT, so the text
                // colour was inherited. In dark mode that inherits near-white
                // onto a light grey panel — the "white text on a very light
                // gray background" the walkthrough reported, and a contrast
                // failure in an accessibility product.
                //
                // Both are now explicit, matching the code blocks on the
                // Integrate tab, which set grey.900/grey.100 and are legible in
                // either mode precisely because they do not inherit.
                bgcolor: 'grey.900',
                color: 'grey.100',
                borderRadius: 1,
                overflowX: 'auto',
                fontSize: '0.8125rem',
                fontFamily: 'monospace',
              }}
            >
              {/* eslint-disable-next-line react/jsx-no-literals -- a code sample, copied verbatim; not prose */}
              {`{
  "scans": [
    {
      "url": "https://example.com/checkout",
      "source": "ci",
      "scannedAt": "2026-03-01T09:30:00.000Z",
      "commitSha": "a1b2c3d",
      "branch": "main",
      "prNumber": 42,
      "externalId": "run-1234",
      "findings": [{ "ruleId": "image-alt", "severity": "critical" }]
    }
  ]
}`}
            </Box>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {t('importHistory.chooseFile')}
              <input
                type="file"
                hidden
                accept=".json,application/json"
                aria-label={t('importHistory.chooseFile')}
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Typography variant="body2" data-testid="selected-file">
                {fileName}
              </Typography>
            )}
          </Stack>

          {parseError && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {parseError}
            </Alert>
          )}

          {parsed && !parseError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('importHistory.ready', { scans: parsed.length, findings: findingsPreview })}
              </Alert>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {submitting
                  ? t('importHistory.importing')
                  : t('importHistory.importButton', { count: parsed.length })}
              </Button>
            </Box>
          )}

          {submitError && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('importHistory.summaryHeading')}
            </Typography>

            <Alert
              severity={result.scansFailed > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              role="status"
            >
              <AlertTitle>{t('importHistory.summaryTitle')}</AlertTitle>
              {t('importHistory.summaryBody', {
                imported: result.scansImported,
                submitted: result.scansSubmitted,
                findings: result.totalFindings,
                failed: result.scansFailed,
              })}
            </Alert>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Chip
                color="success"
                label={t('importHistory.chipImported', { count: result.scansImported })}
              />
              <Chip label={t('importHistory.chipFindings', { count: result.totalFindings })} />
              {result.scansFailed > 0 && (
                <Chip
                  color="error"
                  label={t('importHistory.chipFailed', { count: result.scansFailed })}
                />
              )}
            </Stack>

            {result.overLimit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('importHistory.overLimit')}
              </Alert>
            )}

            {result.errors.length > 0 && (
              <ScrollableTableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label={t('importHistory.errorsTableLabel')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('importHistory.errorScan')}</TableCell>
                      <TableCell>{t('importHistory.errorReason')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e) => (
                      <TableRow key={e.index}>
                        <TableCell>{e.url ?? `#${e.index + 1}`}</TableCell>
                        <TableCell>{e.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
            )}

            {/* REV-732: goes to a URL, so it is a link. */}
            <Button sx={{ mt: 2 }} variant="outlined" component={RouterLink} to={contextHref}>
              {t('importHistory.backToContext')}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default ImportScanHistoryPage;
